import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AutocompleteType } from "../../../types/autoComplete";
import CommonTextField from "../commonTextField/CommonTextField";
import FileDropzone from "../fileDropzone/FileDropzone";
import RichTextMessageEditor from "../richTextMessageEditor/RichTextMessageEditor";
import { SelectAutocompleteSingle } from "../SelectAutocomplete/SelectAutocompleteSingle";
import {
  ACCEPTED_ATTACHMENT_EXTENSIONS,
  MAX_ATTACHMENT_SIZE,
} from "./constants";
import {
  EmailRecipient,
  EmailSenderOption,
  SenderMode,
} from "./sendUserMessageTypes";

const SENDER_OPTIONS: AutocompleteType[] = [
  { id: "personal", name: "sendEmail:senderPersonal" },
  { id: "noreply", name: "sendEmail:senderNoReply" },
];

interface Props {
  subject: string;
  onSubjectChange: (value: string) => void;
  messageHtml: string;
  onMessageHtmlChange: (value: string) => void;
  senderMode: SenderMode;
  selectedSender: EmailSenderOption;
  onSenderChange: (value: EmailSenderOption) => void;
  recipients?: EmailRecipient[];
  translationNamespace?: string;
  attachments: File[];
  onAttachmentsChange: (files: File[]) => void;
  allowAttachments?: boolean;
  onRemoveRecipient?: (id: string) => void;
  sendAsEmail: boolean;
  sendAsInApp: boolean;
  allowSendAsInApp?: boolean;
  onSendAsEmailChange: (value: boolean) => void;
  onSendAsInAppChange: (value: boolean) => void;
}

const SendUserMessageForm = ({
  subject,
  onSubjectChange,
  messageHtml,
  onMessageHtmlChange,
  senderMode,
  selectedSender,
  onSenderChange,
  recipients,
  translationNamespace = "sendEmail",
  attachments,
  onAttachmentsChange,
  allowAttachments = true,
  onRemoveRecipient,
  sendAsEmail,
  sendAsInApp,
  allowSendAsInApp = true,
  onSendAsEmailChange,
  onSendAsInAppChange,
}: Props) => {
  const { t } = useTranslation([translationNamespace, "common"]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  const handleFileSelected = (file: File) => {
    const ext = "." + file.name.split(".").pop()?.toLowerCase();
    if (file.size > MAX_ATTACHMENT_SIZE) {
      setAttachmentError(t(`${translationNamespace}:attachmentFileTooLarge`));
      return;
    }
    if (!ACCEPTED_ATTACHMENT_EXTENSIONS.includes(ext)) {
      setAttachmentError(
        t(`${translationNamespace}:attachmentTypeNotSupported`),
      );
      return;
    }
    setAttachmentError(null);
    onAttachmentsChange([...attachments, file]);
  };

  const selectedSenderOption =
    SENDER_OPTIONS.find((o) => o.id === selectedSender) ?? null;

  const handleSenderChange = (value: AutocompleteType | null) => {
    if (value) {
      onSenderChange(value.id as EmailSenderOption);
    }
  };

  return (
    <>
      {allowSendAsInApp && (
        <Grid item xs={12}>
          <FormGroup
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              columnGap: "8px",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendAsEmail}
                  onChange={(event) =>
                    onSendAsEmailChange(event.target.checked)
                  }
                />
              }
              label={t(`${translationNamespace}:sendAsEmail`)}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendAsInApp}
                  onChange={(event) =>
                    onSendAsInAppChange(event.target.checked)
                  }
                />
              }
              label={t(`${translationNamespace}:sendAsInApp`)}
            />
            {sendAsInApp && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flexBasis: "100%" }}
              >
                {t(`${translationNamespace}:inAppNoReply`)}
              </Typography>
            )}
            {!sendAsEmail && !sendAsInApp && (
              <FormHelperText error sx={{ mx: 0, flexBasis: "100%" }}>
                {t(`${translationNamespace}:channelRequired`)}
              </FormHelperText>
            )}
          </FormGroup>
        </Grid>
      )}
      {senderMode.type === "selectable" && sendAsEmail && (
        <Grid item xs={12}>
          <SelectAutocompleteSingle
            value={selectedSenderOption}
            options={SENDER_OPTIONS}
            onChange={handleSenderChange}
            translationNamespaces={[translationNamespace]}
            disableClearable
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <CommonTextField
          placeholder={t(`${translationNamespace}:subject`)}
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          fullWidth
          size="small"
        />
      </Grid>
      <Grid item xs={12}>
        <Box
          sx={{
            marginTop: "8px",
            "& .message-editor-body": {
              border: "none",
              padding: 0,
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            },
            "& .message-editor-quill .ql-container": {
              height: "calc(100% - 44px)",
            },
          }}
        >
          <RichTextMessageEditor
            value={messageHtml}
            onChange={onMessageHtmlChange}
            height={350}
          />
        </Box>
      </Grid>
      {recipients !== undefined && (
        <Grid item xs={12}>
          <Accordion
            disableGutters
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider" }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">
                {t(`${translationNamespace}:recipients`)} ({recipients.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ maxHeight: 200, overflowY: "auto", p: 1 }}>
              {recipients.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t(`${translationNamespace}:noRecipients`)}
                </Typography>
              ) : (
                recipients.map((r, i) => (
                  <Box
                    key={r.id ?? i}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      {r.name} — {r.email}
                    </Typography>
                    {onRemoveRecipient && r.id && (
                      <IconButton
                        size="small"
                        onClick={() => onRemoveRecipient(r.id as string)}
                        aria-label={t(
                          `${translationNamespace}:removeRecipient`,
                        )}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                ))
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}
      {allowAttachments && (
        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {attachments.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor: "#F3F3F7",
                  borderRadius: "4px",
                  padding: "4px 8px",
                }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {file.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flexShrink: 0 }}
                >
                  {file.size >= 1000 * 1000
                    ? `${(file.size / (1000 * 1000)).toFixed(2)} MB`
                    : `${Math.round(file.size / 1000)} KB`}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setAttachmentError(null);
                    onAttachmentsChange(
                      attachments.filter((_, i) => i !== index),
                    );
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
            <FileDropzone
              onFileSelected={handleFileSelected}
              accept={ACCEPTED_ATTACHMENT_EXTENSIONS.join(",")}
              label={t(`${translationNamespace}:addAttachment`)}
              subLabel={t(`${translationNamespace}:attachmentSubLabel`)}
              sx={{ padding: "12px 16px" }}
            />
            {attachmentError && (
              <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                {attachmentError}
              </Typography>
            )}
          </Box>
        </Grid>
      )}
    </>
  );
};

export default SendUserMessageForm;
